import React, {MouseEvent, useCallback, useMemo, useState} from "react";
import {Box, LinearProgress, Menu} from "@mui/material";
import {DataGrid, GridColumns, GridSortModel} from "@mui/x-data-grid";
import {
    EquipmentRelocatedListSortOrder,
    EquipmentRelocationRequestListSortOrderInitial
} from "store/reactiveVarsStores/equipment-relocate/equipmentRelocatedListSortOrder";
import {SportEquipmentListPagination} from "store/reactiveVarsStores/spor-equipmnts/sportEquipmentListPagination";
import {EquipmentRelocationRequestEntity} from "graphql/graphQlApiHooks";
import {SortDirectionEnum} from "store/reactiveVarsStores/spor-equipmnts/sort-direction.enum";
import {SortEquipmentRelocateColumnsEnum} from "../../../../../store/reactiveVarsStores/equipment-relocate/sort-equipment-relocate-columns.enum";
import MenuItems from "../MenuItems";
import {
    EquipmentCell,
    SportObjectCell,
    StatusCell,
    HistoryCell,
    DateCell,
    ActionsCell
} from "./CellRenderers";

interface DialogsStates {
    equipmentDetails: boolean;
    equipmentRelocationHistory: boolean;
    requestForAdmission: boolean;
    acceptRelocation: boolean;
    rejectRelocation: boolean;
    relocateConfirmation: boolean;
    relocateCompletion: boolean;
    acceptTransportRequest: boolean;
    rejectTransportRequest: boolean;
}

interface Props {
    allCount: number;
    loading: boolean;
    relocateEquipments: EquipmentRelocationRequestEntity[];
    pagination: SportEquipmentListPagination;
    sortOrderModel: EquipmentRelocatedListSortOrder;

    actions: {
        setPagination: (pagination: SportEquipmentListPagination) => void;
        setOrder: (orderModel: EquipmentRelocatedListSortOrder) => void;
    };
}


// TODO:
//    добавить фильтрацию по полю при наведении на ячейку

export const List = (props: Props) => {
    const {relocateEquipments, pagination, allCount, loading, actions: {setPagination, setOrder}} = props;

    const [contextRow, setContextRow] = useState<EquipmentRelocationRequestEntity | null>(null);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);

    const contextMenuHandler = useCallback((event: MouseEvent) => {
        event.preventDefault();
        const row = event.currentTarget;
        const sportEquipmentId = row.getAttribute("data-id");
        setContextRow(
            relocateEquipments.find((item) =>
                item.id === sportEquipmentId
            ) || null
        );
        setContextMenu((prevContextMenu) =>
            prevContextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6
                }
                :
                null
        );
    }, [relocateEquipments]);

    const contextMenuCloseHandler = useCallback(() => {
        setContextMenu(null);
    }, []);

    const sortModelChangeHandler = useCallback((model: GridSortModel) => {
        const sort = model.map((item) => {
            return {
                [item.field]: item.sort.toUpperCase() as SortDirectionEnum
            } as EquipmentRelocatedListSortOrder;
        });
        setOrder(sort?.[0] ? sort?.[0] : EquipmentRelocationRequestListSortOrderInitial);
    }, [setOrder]);

    const handlePageChange = useCallback((page: number) => {
        setPagination({...pagination, page: page});
    }, [pagination, setPagination]);

    const handlePageSizeChange = useCallback((pageSize: number) => {
        setPagination({...pagination, pageSize: pageSize});
    }, [pagination, setPagination]);

    const columns: GridColumns<EquipmentRelocationRequestEntity> = useMemo(() => [
        {
            field: SortEquipmentRelocateColumnsEnum.EquipmentName, headerName: "Оборудование",
            renderCell: (params) => <EquipmentCell row={params.row} />,
            minWidth: 300,
            flex: 2.5,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: SortEquipmentRelocateColumnsEnum.SourceSportObjectName, headerName: "Спортивный объект нахождения",
            renderCell: (params) => <SportObjectCell row={params.row} field="sourceSportObject" emptyText="Спортивный объект не известен" />,
            editable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
        },
        {
            field: SortEquipmentRelocateColumnsEnum.TargetSportObjectName, headerName: "Спортивный объект назначения",
            renderCell: (params) => <SportObjectCell row={params.row} field="targetSportObject" emptyText="Ещё не указан" />,
            editable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
        },
        {
            field: SortEquipmentRelocateColumnsEnum.Status, headerName: "Статус заявки",
            renderCell: (params) => <StatusCell row={params.row} />,
            disableColumnMenu: true,
            editable: false,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: SortEquipmentRelocateColumnsEnum.RejectReason, headerName: "История",
            renderCell: (params) => <HistoryCell params={params} />,
            disableColumnMenu: true,
            sortable: false,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            flex: .5,
        },
        {
            field: SortEquipmentRelocateColumnsEnum.CreatedAt, headerName: "Дата заявки",
            renderCell: (params) => <DateCell row={params.row} />,
            flex: 1,
            editable: false,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            disableColumnMenu: true
        },
        {
            field: "actions", headerName: "Действия",
            renderCell: (params) => <ActionsCell row={params.row} />,
            disableColumnMenu: true,
            sortable: false,
            editable: false,
            align: 'right',
            headerAlign: 'right',
            flex: 2.5,
        }
    ], []);

    return (
        <Box sx={{height: "100%", width: "100%"}}>
            <DataGrid
                rows={relocateEquipments}
                columns={columns}
                getRowId={(row) => row.id}
                getRowHeight={() => 50}
                getRowClassName={({id}) => (id === contextRow?.id && contextMenu !== null) ? "isOpenContextMenu" : ""}
                loading={loading}

                sx={{
                    "& .isOpenContextMenu": {
                        bgcolor: "action.hover"
                    }
                }}

                componentsProps={{
                    row: {
                        onContextMenu: contextMenuHandler,
                        style: {cursor: "context-menu"}
                    }
                }}
                components={{
                    LoadingOverlay: LinearProgress
                }}

                sortingMode='server'
                onSortModelChange={sortModelChangeHandler}

                disableSelectionOnClick={true}
                disableExtendRowFullWidth
                scrollbarSize={8}

                columnBuffer={3}
                columnThreshold={3}
                rowBuffer={5}
                rowThreshold={5}

                pagination
                paginationMode='server'
                rowCount={Math.round(allCount)}
                page={pagination?.page}
                pageSize={pagination?.pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageOptions={[5, 10, 50, 100]}
            />

            <Menu
                open={contextMenu !== null}
                onClose={contextMenuCloseHandler}
                anchorReference='anchorPosition'
                anchorPosition={
                    contextMenu !== null
                        ? {top: contextMenu.mouseY, left: contextMenu.mouseX}
                        : undefined
                }
                elevation={22}
                sx={{
                    maxWidth: 300,
                    "& .MuiListItemText-root .MuiTypography-root": {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        width: "100%",
                        display: "flow-root"
                    }
                }}
            >
                {contextRow && <MenuItems
                    relocateEquipment={contextRow}
                />}
            </Menu>
        </Box>
    );
};